/**
 * ============================================================================
 * CustomerTypeChart - Customer Type Distribution Pie Chart
 * ============================================================================
 */

import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';
import type { CustomerType } from '../../types/customerInsights';

interface CustomerTypeChartProps {
  data: CustomerType[];
}

export function CustomerTypeChart({ data }: CustomerTypeChartProps) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Customer Type Distribution</h3>
        <div className="relative">
          <button
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onFocus={() => setShowInfo(true)}
            onBlur={() => setShowInfo(false)}
            onClick={() => setShowInfo((v) => !v)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Info about customer types"
          >
            <Info className="w-4 h-4" />
          </button>
          {showInfo && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-20">
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
              <p className="font-semibold mb-1">How is this calculated?</p>
              <p><span className="font-medium text-green-300">New:</span> Customers with only 1 booking or membership at your venue.</p>
              <p className="mt-1"><span className="font-medium text-blue-300">Returning:</span> Customers with 2 or more bookings/memberships.</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center">
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0].payload as CustomerType;
                return (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                    <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-600">{item.count} customers ({item.value}%)</p>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-3 mt-6">
        {data.map((type, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }}></div>
              <span className="text-sm text-gray-700 font-semibold">{type.name}</span>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">{type.count} customers</p>
              <p className="text-xs text-gray-500">{type.value}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
